import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { RoomGuestListType } from "@lifeat/shared/data-models/Rooms/RoomGuest";
import { RoomContextType, useRoomContext } from "../../RoomContext";
import { RoomMembersModal } from "../RoomMembers/RoomMembersModal";
import UserRoomPresence from "./UserRoomPresence";
import UserRoomPresenceList from "./UserRoomPresenceList";

const PresenceWrapper = styled.div`
    position: relative;
    height: inherit;
    width: auto;
    display: flex;
    gap: 4px;
`;

const PresenceChildWrapper = styled.div<{ idx: number }>`
    display: flex;
    margin-left: ${(props) => (props.idx > 0 ? `-${AVI_OVERLAP_PX}px` : 0)};
`;

const CONDENSED_GUESTS_THRESHOLD = 4;
const AVI_OVERLAP_PX = 15;

interface RoomPresenceProps {
    readonly roomGuests: RoomGuestListType;
    readonly roomOwnerUid: string | undefined;
}
function RoomPresence(props: RoomPresenceProps) {
    const [showCompressed, setShowCompressed] = useState(false);
    const { isMembersModalOpen, setIsMembersModalOpen }: RoomContextType = useRoomContext();

    const roomGuestsArr = useMemo(() => Object.values(props.roomGuests ?? {}), [props.roomGuests]);

    useEffect(() => {
        setShowCompressed(roomGuestsArr?.length > CONDENSED_GUESTS_THRESHOLD);
    }, [props.roomGuests]);

    const handleToggleMembersModal = () => {
        setIsMembersModalOpen(!isMembersModalOpen);
    };

    const getRoomGuestEls = (): JSX.Element[] => {
        let roomGuestsEls: JSX.Element[] = [];
        for (let i = 0; i < CONDENSED_GUESTS_THRESHOLD; i++) {
            const guest = roomGuestsArr?.[i];
            if (guest != null) {
                roomGuestsEls.push(
                    <PresenceChildWrapper
                        key={`room-presence-user-${guest.userId}`}
                        idx={i}
                        onClick={() => handleToggleMembersModal()}
                    >
                        <UserRoomPresence guest={guest} />
                    </PresenceChildWrapper>,
                );
            }
        }

        if (showCompressed) {
            // Since we exceed the number of Presence icons to show in the control strip, we
            // show the entire guest list in a popover type component.
            roomGuestsEls.push(
                <PresenceChildWrapper
                    key={`room-presence-user-more-guests`}
                    idx={CONDENSED_GUESTS_THRESHOLD}
                    onClick={() => handleToggleMembersModal()}
                >
                    <UserRoomPresenceList
                        numGuests={roomGuestsArr.length}
                        isMembersModalOpen={isMembersModalOpen}
                        setIsMembersModalOpen={setIsMembersModalOpen}
                    />
                </PresenceChildWrapper>,
            );
        }

        return roomGuestsEls;
    };

    return (
        <>
            <PresenceWrapper>{getRoomGuestEls()}</PresenceWrapper>

            <RoomMembersModal
                guests={props.roomGuests}
                roomOwnerUid={props.roomOwnerUid}
                isOpen={isMembersModalOpen}
                handleClose={() => setIsMembersModalOpen(false)}
            />
        </>
    );
}

export default RoomPresence;
